import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import { toast } from 'react-hot-toast';
import { CreditCard, Shield, Lock, ArrowRight } from 'lucide-react';
import { patientAppUrl } from '../../Utils/siteUrls';

function BetaSubscription() {
    const navigate = useNavigate();
    const location = useLocation();
    const { userId: contextUserId, accessToken: contextAccessToken } = useContext(AuthContext);
    const [loading, setLoading] = useState(false);
    const [betaStatus, setBetaStatus] = useState(null);

    const routeState = location.state || {};

    // Get userId and accessToken from route state (post-verification), localStorage, or context
    const getUserData = () => {
        const storedUserId = localStorage.getItem('user_id') || localStorage.getItem('userId');
        const storedToken = localStorage.getItem('access_token') || localStorage.getItem('token');
        return {
            userId: routeState.user_id ?? contextUserId ?? storedUserId,
            accessToken: contextAccessToken ?? storedToken
        };
    };

    useEffect(() => {
        const { userId } = getUserData();
        if (!userId) {
            toast.error('Please verify your email first');
            navigate('/login');
            return;
        }
        // Allow continuing without token when coming from verification (PENDING_PAYMENT_METHOD);
        // backend may accept user_id for checkout session in that flow
        if (!getUserData().accessToken && !routeState.user_id) {
            toast.error('Please verify your email first');
            navigate('/login');
        }
    }, [contextUserId, contextAccessToken, navigate, routeState.user_id]);

    useEffect(() => {
        const loadStatus = async () => {
            const { userId, accessToken } = getUserData();
            if (!userId || !accessToken) return;
            try {
                const baseUrl = process.env.REACT_APP_BACKEND_URL;
                const response = await fetch(`${baseUrl}/beta_billing_status/${userId}/`, {
                    headers: { Authorization: `Bearer ${accessToken}` },
                });
                if (!response.ok) return;
                const data = await response.json();
                setBetaStatus(data);
            } catch (err) {
                // Silent fallback for beta-status card
            }
        };
        loadStatus();
    }, [contextUserId, contextAccessToken]);

    const handleContinue = async () => {
        setLoading(true);
        
        try {
            const { userId, accessToken } = getUserData();
            
            if (!userId) {
                toast.error('Please verify your email first');
                setLoading(false);
                return;
            }

            console.log("Creating Checkout Session for beta subscription, user:", userId);
            
            const baseUrl = process.env.REACT_APP_BACKEND_URL;
            const headers = {
                "Content-Type": "application/json",
                ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
            };
            const body = JSON.stringify({ user_id: userId });

            // Django requires trailing slash (APPEND_SLASH); POST without slash loses body on redirect
            const response = await fetch(`${baseUrl}/create_checkout_session_payment_method/`, {
                method: "POST",
                headers,
                body,
            });

            const data = await response.json().catch(() => ({}));
            console.log("Checkout Session response:", data);

            if (!response.ok) {
                if (data.has_payment_method) {
                    toast.success("You already have a payment method added!");
                    window.location.href = patientAppUrl();
                    return;
                }
                const msg = data.detail || data.error || data.message;
                const code = data.code || (data.detail && data.detail.toLowerCase?.().includes?.("not found") ? "user_not_found" : null);
                if (code === "user_not_found" || (msg && String(msg).toLowerCase().includes("user not found"))) {
                    toast.error("Session expired or user not found. Please verify your email again from the link we sent.");
                    return;
                }
                throw new Error(msg || "Failed to create checkout session");
            }

            if (data.checkout_url) {
                console.log("Redirecting to Stripe Checkout:", data.checkout_url);
                window.location.href = data.checkout_url;
            } else {
                throw new Error(data.error || "No checkout URL returned");
            }
            
        } catch (error) {
            console.error("Error:", error);
            toast.error(error.message || "Failed to load payment page");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 dark:from-[#111920] dark:to-[#1B2731] py-12 px-4">
            <div className="max-w-2xl mx-auto">
                {/* Header */}
                <div className="text-center mb-8">
                    <div className="inline-block bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-200 px-4 py-2 rounded-full text-sm font-semibold mb-4">
                        🎉 Beta Program is Live!
                    </div>
                    <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
                        Join the IzzyAI Beta Program
                    </h1>
                    <p className="text-lg text-gray-600 dark:text-gray-300 max-w-xl mx-auto">
                        Experience the future of speech therapy. During the beta period, you'll have full access to all features at no cost.
                    </p>
                </div>

                {/* Main Card */}
                <div className="bg-white dark:bg-[#222a32] rounded-2xl shadow-xl p-8 md:p-10 mb-8">
                    {/* Important Notice */}
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400 p-4 mb-6 rounded">
                        <p className="text-sm text-yellow-800 dark:text-yellow-200">
                            <strong>Important:</strong> During the beta program, you cannot choose any other subscription plan. 
                            To avail the beta program, you need to enter your payment details below. 
                            <strong>Your card will not be charged during the beta period.</strong>
                        </p>
                    </div>

                    {/* Beta Benefits */}
                    <div className="mb-8">
                        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                            What You Get in Beta
                        </h2>
                        <div className="space-y-4">
                            <div className="flex items-start gap-3">
                                <div className="bg-green-100 dark:bg-green-900/30 rounded-full p-2 mt-1">
                                    <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                </div>
                                <div>
                                    <h3 className="font-semibold text-gray-900 dark:text-white">Full Access to All Features</h3>
                                    <p className="text-gray-600 dark:text-gray-300 text-sm">Unlimited access to all AI therapy features and tools</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-3">
                                <div className="bg-green-100 dark:bg-green-900/30 rounded-full p-2 mt-1">
                                    <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                </div>
                                <div>
                                    <h3 className="font-semibold text-gray-900 dark:text-white">24/7 AI-Powered Therapy</h3>
                                    <p className="text-gray-600 dark:text-gray-300 text-sm">Your Speech Guardian available anytime, anywhere</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-3">
                                <div className="bg-green-100 dark:bg-green-900/30 rounded-full p-2 mt-1">
                                    <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                </div>
                                <div>
                                    <h3 className="font-semibold text-gray-900 dark:text-white">Personalized Progress Tracking</h3>
                                    <p className="text-gray-600 dark:text-gray-300 text-sm">Track your growth with detailed insights and analytics</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-3">
                                <div className="bg-green-100 dark:bg-green-900/30 rounded-full p-2 mt-1">
                                    <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                </div>
                                <div>
                                    <h3 className="font-semibold text-gray-900 dark:text-white">Interactive Therapy Games</h3>
                                    <p className="text-gray-600 dark:text-gray-300 text-sm">Engaging exercises designed to support your growth</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Security Info */}
                    {betaStatus && (
                        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 mb-6">
                            <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Your Beta Billing Status</h3>
                            <p className="text-sm text-gray-700 dark:text-gray-300">
                                Payment method: {betaStatus.has_payment_method ? "Saved" : "Not saved"}{" "}
                                {betaStatus.upcoming_charge_date ? `• Upcoming charge date: ${new Date(betaStatus.upcoming_charge_date).toLocaleDateString()}` : ""}
                            </p>
                        </div>
                    )}

                    <div className="bg-gray-50 dark:bg-[#1B2731] rounded-lg p-4 mb-6">
                        <div className="flex items-start gap-3">
                            <Shield className="w-5 h-5 text-cyan-600 mt-1 flex-shrink-0" />
                            <div>
                                <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Secure Payment Processing</h3>
                                <p className="text-sm text-gray-600 dark:text-gray-300">
                                    Your payment information is securely processed by Stripe. We use industry-standard encryption to protect your data.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Continue Button */}
                    <button
                        onClick={handleContinue}
                        disabled={loading}
                        className="w-full bg-gradient-to-r from-[#0CC8E8] to-[#2DEEAA] text-white font-bold py-4 px-6 rounded-full hover:opacity-90 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                        {loading ? (
                            <>
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                <span>Processing...</span>
                            </>
                        ) : (
                            <>
                                <span>Continue to Payment Setup</span>
                                <ArrowRight className="w-5 h-5" />
                            </>
                        )}
                    </button>

                    {/* Footer Note */}
                    <p className="text-xs text-center text-gray-500 dark:text-gray-400 mt-6">
                        <Lock className="w-3 h-3 inline mr-1" />
                        Secured by Stripe • Your card will not be charged during the beta period
                    </p>
                </div>

                {/* Additional Info */}
                <div className="text-center">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                        Questions? Contact us at{' '}
                        <a href="mailto:support@izzyai.com" className="text-cyan-600 dark:text-cyan-400 hover:underline">
                            support@izzyai.com
                        </a>
                    </p>
                </div>
            </div>
        </div>
    );
}

export default BetaSubscription;





























